import React, { useState, useEffect, useRef } from 'react';
import { Box, Stack, Typography, Button, Card, CardContent, Divider, CardActions } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import parse, { DOMNode } from 'html-react-parser';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';
import { DTBContentBlock } from '../../../_types/dtb';
import BlockSelector from './BlockSelector';
import { translate as t } from 'hooks/useTranslations';

interface SortableBlockProps {
  block: DTBContentBlock;
  index: number;
  onSave: (index: number, block: DTBContentBlock) => void;
  isReorderMode: boolean;
  onDelete: (index: number) => void;
  isNewlyAdded?: boolean;
  onEditModeChange?: (isEditMode: boolean) => void;
  dtbLevel: 'AGENCY' | 'JURISDICTION' | 'GLOBAL';
}

const SortableBlock: React.FC<SortableBlockProps> = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `content-${props.index}`,
  });

  const cardRef = useRef<HTMLDivElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isEditMode, setIsEditMode] = useState(props.isNewlyAdded || false);

  useEffect(() => {
    if (props.onEditModeChange) {
      props.onEditModeChange(isEditMode);
    }
  }, [isEditMode, props.onEditModeChange]);

  useEffect(() => {
    if (props.isNewlyAdded && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [props.isNewlyAdded]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleDone = () => {
    setIsEditMode(false);
  };

  return (
    <Card
      ref={(node) => {
        setNodeRef(node);
        if (node) {
          (cardRef as React.MutableRefObject<HTMLDivElement>).current = node as HTMLDivElement;
        }
      }}
      style={style}
      className='lex-form-card'
      sx={{ mb: 2 }}
    >
      <CardContent>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: props.isReorderMode ? 'auto 1fr' : '1fr auto',
            alignItems: 'center',
            gap: 2,
            paddingY: 3,
            paddingX: 4,
          }}
        >
          {props.isReorderMode && (
            <Box
              {...attributes}
              {...listeners}
            >
              <DragIndicatorIcon />
            </Box>
          )}
          <Stack
            direction='row'
            spacing={1}
          >
            {!props.isReorderMode && (
              <Button
                variant='text'
                color='neutral'
                size='small'
                className='xs icon-only'
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCollapsed(!isCollapsed);
                }}
                sx={{ marginLeft: '-8px !important' }}
              >
                {isCollapsed ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
              </Button>
            )}
            <Typography
              variant='subtitle1'
              sx={{ cursor: props.isReorderMode ? 'grab' : 'default', width: '100%' }}
            >
              {parse(props.block.title || 'Selection title', {
                replace(node: DOMNode, ind: number) {
                  return PolicyContentHelper.handleContent(node, ind);
                },
              })}
            </Typography>
          </Stack>
          {!props.isReorderMode && (
            <Stack
              direction='row'
              spacing={1}
            >
              <Button
                variant='outlined'
                color='error'
                size='small'
                className='xs icon-only'
                onClick={(e) => {
                  e.stopPropagation();
                  props.onDelete(props.index);
                }}
              >
                <DeleteIcon />
              </Button>
              {!(props.block.contentType === 'VIDEO' && props.dtbLevel === 'AGENCY') && (
                <Button
                  variant='outlined'
                  color='neutral'
                  size='small'
                  className='xs icon-only'
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditMode(!isEditMode);
                  }}
                >
                  <EditIcon />
                </Button>
              )}
            </Stack>
          )}
        </Box>
        {!props.isReorderMode && !isCollapsed && (
          <>
            <Divider />
            <Box className='lex-form-card-content'>
              <BlockSelector
                onSave={props.onSave}
                index={props.index}
                block={props.block}
                isEditMode={isEditMode}
              />
            </Box>
          </>
        )}
      </CardContent>
      {!props.isReorderMode && !isCollapsed && isEditMode && (
        <CardActions sx={{ justifyContent: 'flex-end', paddingX: 4, paddingY: 2 }}>
          <Button
            variant='contained'
            color='primary'
            size='small'
            onClick={handleDone}
          >
            {t('common.done')}
          </Button>
        </CardActions>
      )}
    </Card>
  );
};

export default SortableBlock;
